/**
 * ADLResidentSelector.jsx
 * CCH Care — Phase 3
 * Last Modified: 2026-03-26
 *
 * Changes:
 * - Shows assigned residents as tappable cards immediately (no search needed)
 * - "Other Residents" section collapsible — for helping a colleague
 * - Search bar filters across both groups simultaneously
 * - Alert badges shown on resident cards (matches dashboard style)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import useResidentADLList from '../../hooks/useResidentADLList';

const SHIFT_TYPES = ['AM', 'PM', 'NOC'];

function defaultShift() {
  const h = new Date().getHours();
  if (h >= 7  && h < 15) return 'AM';
  if (h >= 15 && h < 23) return 'PM';
  return 'NOC';
}

// ── Alert badge colours matching dashboard ────────────────────
const ALERT_COLORS = {
  fall_risk:            { bg: '#FFF3CD', color: '#856404', label: 'Fall Risk'       },
  elopement_risk:       { bg: '#F8D7DA', color: '#721C24', label: 'Elopement Risk'  },
  skin_check_fail:      { bg: '#F8D7DA', color: '#721C24', label: 'Skin Issue'      },
  low_fluid_intake:     { bg: '#D1ECF1', color: '#0C5460', label: 'Low Fluids'      },
  missed_repositioning: { bg: '#FFF3CD', color: '#856404', label: 'Reposition'      },
  meal_refused:         { bg: '#FFF3CD', color: '#856404', label: 'Meal Refused'    },
  custom:               { bg: '#E2E3E5', color: '#383D41', label: 'Alert'           },
};

function AlertBadge({ type, label }) {
  const cfg = ALERT_COLORS[type] ?? ALERT_COLORS.custom;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      {label ?? cfg.label}
    </span>
  );
}

function ADLStatusChip({ resident }) {
  if (!resident.adl_record_id) return (
    <span style={{ fontSize: 11, color: COLORS.textMuted }}>No ADL started</span>
  );
  if (resident.adl_complete) return (
    <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>✓ ADL Complete</span>
  );
  return (
    <span style={{ fontSize: 11, color: COLORS.warning, fontWeight: 600 }}>⏳ In Progress</span>
  );
}

// ── Resident card ────────────────────────────────────────────
function ResidentCard({ resident, onSelect, isOther = false, isLast = false }) {
  const initials = `${resident.first_name?.[0] ?? ''}${resident.last_name?.[0] ?? ''}`;
  const alerts   = resident.alerts ?? [];

  return (
    <button
      onClick={() => onSelect(resident)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 16px', background: 'none', border: 'none',
        borderBottom: !isLast ? `1px solid ${COLORS.border}` : 'none',
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        background: isOther ? COLORS.bg : COLORS.primaryLight,
        color: isOther ? COLORS.textMuted : COLORS.primary,
        border: `2px solid ${isOther ? COLORS.border : COLORS.primaryLight}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700,
      }}>
        {initials}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
            {resident.first_name} {resident.last_name}
          </span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            Rm {resident.room_number ?? '—'}
            {resident.unit_name ? ` · ${resident.unit_name}` : ''}
          </span>
        </div>
        {alerts.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 3 }}>
            {alerts.map((a, i) => (
              <AlertBadge key={i} type={a.alert_type} label={a.label ?? a.alert_type?.replace(/_/g, ' ')} />
            ))}
          </div>
        )}
        <ADLStatusChip resident={resident} />
      </div>

      {/* Colleague tag for unassigned */}
      {isOther && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
          background: COLORS.bg, border: `1px solid ${COLORS.border}`,
          borderRadius: 4, padding: '2px 6px', flexShrink: 0,
        }}>
          Colleague&apos;s
        </span>
      )}

      <span style={{ fontSize: 18, color: COLORS.primary, flexShrink: 0 }}>›</span>
    </button>
  );
}

// ── Section header ────────────────────────────────────────────
function SectionHeader({ label, count, collapsible, collapsed, onToggle }) {
  return (
    <div
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      onClick={collapsible ? onToggle : undefined}
      onKeyDown={collapsible ? e => (e.key === 'Enter' || e.key === ' ') && onToggle() : undefined}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: COLORS.bg,
        borderBottom: `1px solid ${COLORS.border}`,
        cursor: collapsible ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: COLORS.primary,
          background: COLORS.primaryLight, borderRadius: 10,
          padding: '1px 7px',
        }}>
          {count}
        </span>
      </div>
      {collapsible && (
        <span style={{
          fontSize: 12, color: COLORS.textMuted,
          transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
          transition: 'transform 0.2s', display: 'inline-block',
        }}>▼</span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function ADLResidentSelector({ onSelect }) {
  const navigate = useNavigate();

  const [shiftType, setShiftType] = useState(defaultShift);
  const [othersExpanded, setOthersExpanded] = useState(false);

  const {
    assignedResidents, otherResidents,
    loading, error,
    searchQuery, setSearchQuery, searching, isSearching,
  } = useResidentADLList(shiftType);

  function handleSelect(resident) {
    const payload = {
      care_shift_id: null,
      shift_type:    shiftType,
      shift_date:    new Date().toISOString().slice(0, 10),
    };
    if (onSelect) {
      onSelect(resident, payload);
    } else {
      navigate(`/dashboard/adl/${resident.patient_id}`, {
        state: { patient_id: resident.patient_id, first_name: resident.first_name, last_name: resident.last_name, ...payload },
      });
    }
  }

  function handleShiftChange(st) {
    setShiftType(st);
    setOthersExpanded(false);
    setSearchQuery('');
  }

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', fontSize: 20,
          cursor: 'pointer', color: COLORS.primary, padding: '2px 6px',
        }}>←</button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Start ADL Flow Sheet</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Select a resident</div>
        </div>
      </div>

      <div style={{ padding: 16 }}>

        {/* Shift selector */}
        <div style={{
          background: COLORS.surface, borderRadius: 12, padding: 14,
          border: `1px solid ${COLORS.border}`, marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, marginBottom: 10, letterSpacing: 0.5 }}>
            SHIFT
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {SHIFT_TYPES.map(st => (
              <button key={st} onClick={() => handleShiftChange(st)} style={{
                flex: 1, padding: '10px 0', borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                background: shiftType === st ? COLORS.primary : COLORS.bg,
                color: shiftType === st ? '#fff' : COLORS.textMuted,
                border: `1px solid ${shiftType === st ? COLORS.primary : COLORS.border}`,
                transition: 'all 0.15s',
              }}>
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <div style={{
          background: COLORS.surface, borderRadius: 12,
          border: `1px solid ${isSearching ? COLORS.primary : COLORS.border}`,
          marginBottom: 14,
          display: 'flex', alignItems: 'center', padding: '4px 14px', gap: 8,
          transition: 'border-color 0.15s',
        }}>
          <span style={{ fontSize: 16, color: COLORS.textMuted }}>🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name or room number…"
            style={{
              flex: 1, border: 'none', outline: 'none', padding: '10px 0',
              fontSize: 15, color: COLORS.text, background: 'none', fontFamily: 'inherit',
            }}
          />
          {searching && <span style={{ fontSize: 12, color: COLORS.textMuted }}>…</span>}
          {searchQuery.length > 0 && (
            <button onClick={() => setSearchQuery('')} style={{
              background: 'none', border: 'none', fontSize: 16,
              color: COLORS.textLight, cursor: 'pointer', padding: 0,
            }}>✕</button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '30px 20px', color: COLORS.textMuted }}>
            <div style={{ fontSize: 13 }}>Loading residents…</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerAlpha}`,
            borderRadius: 10, padding: '12px 16px', marginBottom: 12,
            fontSize: 13, color: COLORS.danger, fontWeight: 600,
          }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && (
          <>
            {/* ── Your Residents ── */}
            <div style={{
              background: COLORS.surface, borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
              overflow: 'hidden', marginBottom: 12,
            }}>
              <SectionHeader
                label="Your Residents"
                count={assignedResidents.length}
                collapsible={false}
              />

              {assignedResidents.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: COLORS.textMuted }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {isSearching
                      ? 'No assigned residents match your search'
                      : `No residents assigned to you for the ${shiftType} shift`
                    }
                  </div>
                </div>
              ) : (
                assignedResidents.map((r, i) => (
                  <ResidentCard
                    key={r.patient_id}
                    resident={r}
                    onSelect={handleSelect}
                    isLast={i === assignedResidents.length - 1}
                  />
                ))
              )}
            </div>

            {/* ── Other Residents ── */}
            {(otherResidents.length > 0 || isSearching) && (
              <div style={{
                background: COLORS.surface, borderRadius: 12,
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
              }}>
                <SectionHeader
                  label="Other Residents"
                  count={otherResidents.length}
                  collapsible={!isSearching}
                  collapsed={!othersExpanded && !isSearching}
                  onToggle={() => setOthersExpanded(e => !e)}
                />

                {!othersExpanded && !isSearching && (
                  <div style={{ padding: '12px 16px', fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic' }}>
                    Tap to expand — use this to help a colleague&apos;s resident.
                  </div>
                )}

                {(othersExpanded || isSearching) && (
                  <>
                    {otherResidents.length === 0 ? (
                      <div style={{ padding: '24px 16px', textAlign: 'center', color: COLORS.textMuted }}>
                        <div style={{ fontSize: 13 }}>No other residents found</div>
                      </div>
                    ) : (
                      otherResidents.map((r, i) => (
                        <ResidentCard
                          key={r.patient_id}
                          resident={r}
                          onSelect={handleSelect}
                          isOther
                          isLast={i === otherResidents.length - 1}
                        />
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}